import { useEffect, useRef, useState } from "react";
import { motion, useInView, useMotionValue, useSpring, useTransform, animate } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, y = 40, className = "", as = "div" }) => {
  const M = motion[as];
  return (
    <M className={className} initial={{ opacity: 0, y, filter: "blur(8px)" }} whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      viewport={{ once: true, margin: "-60px" }} transition={{ duration: 1, ease: EASE, delay }}>
      {children}
    </M>
  );
};

const WORD = { hidden: { opacity: 0, y: "0.6em", filter: "blur(6px)" }, show: { opacity: 1, y: 0, filter: "blur(0px)" } };

export const SplitText = ({ text = "", className = "", delay = 0, stagger = 0.06 }) => {
  const words = text.split(/\s+/).filter(Boolean);
  return (
    <motion.span key={text} className={className} aria-label={text} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.1 }}
      transition={{ staggerChildren: stagger, delayChildren: delay }}>
      {words.map((w, i) => (
        <span key={i}>
          <motion.span className="inline-block" variants={WORD} transition={{ duration: 0.9, ease: EASE }}>{w}</motion.span>
          {i < words.length - 1 && " "}
        </span>
      ))}
    </motion.span>
  );
};

export const Magnetic = ({ children, strength = 0.35, className = "" }) => {
  const ref = useRef(null);
  const x = useSpring(useMotionValue(0), { stiffness: 220, damping: 15 });
  const y = useSpring(useMotionValue(0), { stiffness: 220, damping: 15 });
  const move = (e) => {
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - r.left - r.width / 2) * strength);
    y.set((e.clientY - r.top - r.height / 2) * strength);
  };
  return (
    <motion.div ref={ref} style={{ x, y }} onMouseMove={move} onMouseLeave={() => { x.set(0); y.set(0); }} className={`inline-block ${className}`}>
      {children}
    </motion.div>
  );
};

export const Tilt = ({ children, className = "" }) => {
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 180, damping: 18 });
  const ry = useSpring(useTransform(mx, [0, 1], [-7, 7]), { stiffness: 180, damping: 18 });
  const glare = useTransform([mx, my], ([a, b]) => `radial-gradient(circle at ${a * 100}% ${b * 100}%, rgba(255,240,210,0.35), transparent 55%)`);
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  return (
    <motion.div onMouseMove={move} onMouseLeave={() => { mx.set(0.5); my.set(0.5); }} style={{ rotateX: rx, rotateY: ry, transformPerspective: 900 }} className={`relative ${className}`}>
      {children}
      <motion.div style={{ background: glare }} className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    </motion.div>
  );
};

export const CountUp = ({ to, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const c = animate(0, to, { duration: 2.2, ease: EASE, onUpdate: (n) => setV(Math.round(n)) });
    return () => c.stop();
  }, [inView, to]);
  return <span ref={ref}>{v}{suffix}</span>;
};
